import React from "react";
import { ScanLine, Wrench, CarFront, Armchair, Gauge, CircleCheck } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const steps = [{
  title: "Diagnostic",
  icon: ScanLine,
  description: "Full electronic system scan & analysis"
}, {
  title: "Mechanical",
  icon: Wrench,
  description: "Engine, transmission & drivetrain check"
}, {
  title: "Exterior",
  icon: CarFront,
  description: "Bodywork, paint & structural inspection"
}, {
  title: "Interior",
  icon: Armchair,
  description: "Upholstery, controls & safety features"
}, {
  title: "Road Test",
  icon: Gauge,
  description: "Dynamic performance & handling verification"
}];
export default function OurInspectionProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="our-inspection-process" ref={ref} className={cn("dark bg-background text-foreground py-20 px-4 md:px-8 overflow-hidden relative", isVisible ? "opacity-100" : "opacity-0 transition-opacity duration-700")}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[-10%] left-[-5%] w-96 h-96 bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        <div className="text-center mb-16 max-w-3xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
            Our Inspection Process
          </h2>
          <p className="text-muted-foreground text-lg">
            Every vehicle in our fleet undergoes a rigorous 5-step certification
            process to ensure reliability, safety, and peak performance.
          </p>
        </div>

        {}
        <div className="hidden lg:flex items-start justify-between w-full">
          {steps.map((step, i) => <React.Fragment key={i}>
              <div data-index={i} className={cn("flex flex-col items-center text-center gap-4 w-48 shrink-0 transition-all duration-700 delay-[calc(100ms*var(--index))]", isVisible ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0")} style={{
            "--index": i
          } as React.CSSProperties}>
                <div className="relative group">
                  <div className="absolute inset-0 bg-primary/20 rounded-xl blur-md group-hover:bg-primary/40 transition-all duration-500" />
                  <div className="relative bg-card border border-border p-4 rounded-xl shadow-lg group-hover:border-primary/50 transition-colors duration-300">
                    <step.icon className="w-8 h-8 text-primary" />
                  </div>
                  <div className="absolute -top-2 -right-2 bg-background rounded-full p-0.5 border border-border">
                    <CircleCheck className="w-4 h-4 text-primary fill-primary/20" />
                  </div>
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-1">{step.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {step.description}
                  </p>
                </div>
              </div>

              {}
              {i < steps.length - 1 && <div className="flex-1 pt-8 px-2">
                  <Progress value={100} className="h-0.5 bg-border" aria-label={`Step ${i + 1} completed`} />
                </div>}
            </React.Fragment>)}
        </div>

        {}
        <div className="lg:hidden flex flex-col gap-8 max-w-md mx-auto">
          {steps.map((step, i) => <div key={i} data-index={i} className={cn("flex gap-6 relative transition-all duration-700 delay-[calc(100ms*var(--index))]", isVisible ? "translate-x-0 opacity-100" : "translate-x-8 opacity-0")} style={{
          "--index": i
        } as React.CSSProperties}>
              {}
              {i < steps.length - 1 && <div className="absolute left-6 top-16 bottom-[-32px] w-0.5 bg-border" />}

              <div className="relative shrink-0">
                <div className="w-12 h-12 rounded-xl bg-card border border-border flex items-center justify-center shadow-sm z-10 relative">
                  <step.icon className="w-6 h-6 text-primary" />
                </div>
              </div>

              <div className="pt-1 pb-4">
                <h3 className="font-semibold text-lg mb-1 flex items-center gap-2">
                  {step.title}
                  <span className="text-xs font-normal text-primary bg-primary/10 px-2 py-0.5 rounded-full">
                    Step {i + 1}
                  </span>
                </h3>
                <p className="text-muted-foreground text-sm">
                  {step.description}
                </p>
              </div>
            </div>)}
        </div>

        <div className="mt-16 text-center">
          <Button asChild size="lg" className="font-semibold">
            <Link to="/RentalFleetPage">View Certified Fleet</Link>
          </Button>
        </div>
      </div>
    </section>;
}